import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, BellRing, CheckCircle2, ChevronDown, ChevronUp, ClipboardList, Hammer, Inbox, Plus, Search, Unlink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { HeroBanner, StatCard } from "../components/HeroBanner";
import { TaskCard } from "../components/tasks/TaskCard";
import { TaskCreateDialog } from "../components/tasks/TaskCreateDialog";
import { AdminGroupChat } from "../components/tasks/AdminGroupChat";
import { AdminAbsenCard, AdminAttendanceList } from "../components/tasks/AdminAbsen";
import { AdminPerformance } from "../components/tasks/AdminPerformance";

const FILTERS = [["", "Baru"], ["diterima", "Diterima"], ["dikerjakan", "Dikerjakan"], ["selesai", "Selesai"], ["ditolak", "Ditolak"]];

export default function AdminTasks() {
  const { user: me } = useAuth();
  const isSuper = me?.role === "admin" && !!me?.is_owner;
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [onlyLate, setOnlyLate] = useState(false);
  const [dlg, setDlg] = useState(null);
  const [resetReqs, setResetReqs] = useState(0);
  const [reminding, setReminding] = useState(false);
  const [attKey, setAttKey] = useState(0);
  const [openMonths, setOpenMonths] = useState({});
  const [admins, setAdmins] = useState([]);
  useEffect(() => { if (isSuper) api.get("/admin-tasks/admins").then((r) => setAdmins(r.data)).catch(() => {}); }, [isSuper]);
  useEffect(() => { api.get("/reset-requests/count").then((r) => setResetReqs(r.data.count)).catch(() => {}); }, []);
  const load = useCallback(() => api.get("/admin-tasks", { params: { status } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [status]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const s = data?.summary || {};
  const tasks = useMemo(() => (data?.tasks || []).filter((t) => (status !== "" || !t.reviewed_at) && (!onlyLate || t.overdue) && (!q || `${t.title} ${t.detail} ${t.assignee_names.join(" ")}`.toLowerCase().includes(q.toLowerCase()))), [data, q, onlyLate, status]);

  const remindDue = async () => {
    setReminding(true);
    try {
      const { data: r } = await api.post("/admin-tasks/remind-due", { days: 2 });
      toast.success(r.sent ? `Popup pengingat dikirim ke ${r.sent} admin (${r.tasks} tugas)` : "Tidak ada tugas terlambat/mendekati deadline yang belum diingatkan");
    } catch (e) { toast.error(errMsg(e)); } finally { setReminding(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-tasks-page">
      <HeroBanner icon={ClipboardList} title="Tugas Admin" subtitle="Pembagian tugas, target waktu, progres & laporan tim admin" testid="tasks-hero">
        {resetReqs > 0 && (
          <Link to="/reset-voucher" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-600 dark:text-amber-400 text-xs font-semibold hover:bg-amber-500/25" data-testid="tasks-reset-requests-link">
            <Unlink className="w-3.5 h-3.5" /> {resetReqs} pengajuan reset voucher
          </Link>
        )}
        {isSuper && <Button size="sm" variant="outline" disabled={reminding} onClick={remindDue} className="h-9 border-[#F04E23]/50 text-[#F04E23] hover:bg-[#F04E23]/10" data-testid="task-remind-due-btn"><BellRing className="w-4 h-4 mr-1.5" /> Ingatkan Deadline</Button>}
        {isSuper && <Button size="sm" onClick={() => setDlg("new")} className="h-9 bg-emerald-500 hover:bg-emerald-600 text-white shadow" data-testid="task-create-btn"><Plus className="w-4 h-4 mr-1" /> Buat Tugas</Button>}
      </HeroBanner>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="task-summary">
        <StatCard icon={Inbox} label="Tugas Baru" value={s.baru ?? 0} hint={`${s.diterima ?? 0} diterima`} tone="blue" testid="task-sum-baru" active={status === "baru"} onClick={() => { setOnlyLate(false); setStatus(""); }} />
        <StatCard icon={Hammer} label="Sedang Dikerjakan" value={s.dikerjakan ?? 0} hint="progres berjalan" tone="purple" testid="task-sum-dikerjakan" active={status === "dikerjakan"} onClick={() => { setOnlyLate(false); setStatus(status === "dikerjakan" ? "" : "dikerjakan"); }} />
        <StatCard icon={CheckCircle2} label="Selesai" value={s.selesai ?? 0} hint={`${s.ditolak ?? 0} ditolak`} tone="green" testid="task-sum-selesai" active={status === "selesai"} onClick={() => { setOnlyLate(false); setStatus(status === "selesai" ? "" : "selesai"); }} />
        <StatCard icon={AlertTriangle} label="Terlambat" value={s.terlambat ?? 0} hint="lewat target" tone="orange" testid="task-sum-terlambat" active={onlyLate} onClick={() => { setStatus(""); setOnlyLate(!onlyLate); }} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1" data-testid="task-filters">
          {FILTERS.map(([v, l]) => <button key={v} type="button" onClick={() => { setOnlyLate(false); setStatus(v); }} className={`px-3 py-1 rounded-full text-xs border transition-colors ${status === v && !onlyLate ? "bg-[#F04E23] text-white border-[#F04E23]" : "border-border bg-card hover:bg-accent"}`} data-testid={`task-filter-${v || "all"}`}>{l}</button>)}
        </div>
        <div className="relative flex-1 min-w-48 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari judul, detail, nama admin…" className="h-9 pl-9 bg-card" data-testid="task-search" />
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-5 xl:items-start">
        <div className="xl:col-span-3 space-y-3">
          {data && tasks.length === 0 && <div className="text-sm text-muted-foreground border border-dashed border-border rounded-2xl p-10 text-center bg-card/50" data-testid="task-empty">Belum ada tugas.</div>}
          {status === "selesai" ? (
            <div className="space-y-3" data-testid="task-archive">
              {Object.entries(tasks.reduce((acc, t) => { const k = (t.done_at || t.created_at || "").slice(0, 7); (acc[k] = acc[k] || []).push(t); return acc; }, {})).sort((a, b) => b[0].localeCompare(a[0])).map(([ym, list], idx) => {
                const label = new Date(`${ym}-01T00:00:00`).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
                const isOpen = openMonths[ym] ?? idx === 0;
                return (
                  <div key={ym} className="rounded-2xl border border-border bg-card/60 overflow-hidden" data-testid={`task-archive-${ym}`}>
                    <button type="button" onClick={() => setOpenMonths({ ...openMonths, [ym]: !isOpen })} className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold hover:bg-accent/50" data-testid={`task-archive-toggle-${ym}`}>
                      <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> {label}</span>
                      <span className="flex items-center gap-2 text-xs text-muted-foreground"><span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 font-mono">{list.length} tugas</span>{isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
                    </button>
                    {isOpen && <div className="grid gap-3 md:grid-cols-2 p-3 pt-0">{list.map((t) => <TaskCard key={t.id} task={t} me={me} onChange={load} onEdit={isSuper ? () => setDlg(t) : null} />)}</div>}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {tasks.map((t) => <TaskCard key={t.id} task={t} me={me} onChange={load} onEdit={isSuper ? () => setDlg(t) : null} />)}
            </div>
          )}
        </div>
        <div className="xl:col-span-2 space-y-4">
          {!isSuper && <AdminAbsenCard onChanged={() => setAttKey((k) => k + 1)} />}
          <AdminGroupChat isOwner={isSuper} pendingTasks={(s.baru || 0) + (s.diterima || 0) + (s.dikerjakan || 0)} onReminded={load} />
          <AdminAttendanceList isSuper={isSuper} admins={admins} refreshKey={attKey} />
          {isSuper && <AdminPerformance />}
        </div>
      </div>

      {isSuper && <TaskCreateDialog open={!!dlg} task={dlg && dlg !== "new" ? dlg : null} onClose={() => setDlg(null)} onCreated={() => { setDlg(null); load(); }} />}
    </div>
  );
}
